import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { User, UserStatus } from 'api/models';
import { Color } from 'components/colors';
import { AsyncSelect } from 'components/facade/select';
import { GroupComponentForSelect } from 'components/molecules/group-select/group-select';
import { useAccountFeature, AccountFeature } from 'hooks/use-account-feature';
import { useTranslate } from 'hooks/use-translation';
import { useGroupsQuery } from 'queries/groups';
import { useAsyncNativeUsers } from 'queries/members';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { UserComponents, TagComponents } from './select';
import { CustomOption, Props, GroupType } from './types';

const selectStyles = {
  valueContainer: (base) => ({
    ...base,
    overflow: 'scroll',
  }),
};

export interface UserOption extends CustomOption {
  type: 'user';
  imageUrl?: string;
  userStatus?: UserStatus;
  description?: string;
}

export interface GroupOption extends CustomOption {
  type: 'group';
  color?: Color;
  memberCount?: number;
}

export interface UserGroupSelectProps
  extends Omit<Props, 'options' | 'components'> {
  users?: UserOption[];
  groups?: GroupOption[];
  showUsers?: boolean;
  showGroups?: boolean;
  filterFnUsers?: (user: User) => boolean;
}

// Custom option component that renders differently for users vs groups
const UserGroupOption = (props: any) => {
  const { data } = props;

  if (data.type === 'user') {
    return <UserComponents.Option {...props} />;
  }
  if (data.type === 'group') {
    return <TagComponents.Option {...props} />;
  }

  return <div>{data.label}</div>;
};

// Custom single value component that renders differently for users vs groups
const UserGroupSingleValue = (props: any) => {
  const { data } = props;

  if (data.type === 'user') {
    return <UserComponents.SingleValue {...props} />;
  }
  if (data.type === 'group') {
    return <TagComponents.SingleValue {...props} />;
  }

  return <div>{data.label}</div>;
};

// Custom multi value component that renders differently for users vs groups
const UserGroupMultiValue = (props: any) => {
  const { data } = props;

  if (data.type === 'user') {
    return <UserComponents.MultiValue {...props} />;
  }
  if (data.type === 'group') {
    return <TagComponents.MultiValue {...props} />;
  }

  return <div>{data.label}</div>;
};

const UserGroupMultiValueLabel = (props: any) => {
  const { data } = props;

  if (['user', 'group'].includes(data.type)) {
    return (
      <UserComponents.MultiValueLabel
        {...props}
        data={{
          ...data,
          hideAvatar:
            (data.type && data.type === 'group') ?? data.hideAvatar ?? false,
        }}
      />
    );
  }

  return <div>{data.label}</div>;
};

export const UserGroupSelect: FC<UserGroupSelectProps> = ({
  showUsers = true,
  showGroups = true,
  filterFnUsers,
  ...selectProps
}) => {
  // Fetch users using paginated API with async search
  const { defaultOptions: users, loadOptions: loadUserOptions } =
    useAsyncNativeUsers(filterFnUsers, undefined, true);
  const { data: groups = [] } = useGroupsQuery();
  const [restrictUserGroupsFeature] = useAccountFeature(
    AccountFeature.RestrictUserGroups,
  );

  const translate = useTranslate();

  // Transform users for the UserGroupSelect
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        type: 'user' as const,
        label: fullName(user),
        value: `user_${user.userId}`,
        imageUrl: user.photoUrl,
        userStatus: user.userStatus,
        description: user.role?.roleName,
        userId: user.userId,
      })),
    [users],
  );

  const groupOptions = useMemo(
    () =>
      groups.map((group) => ({
        type: 'group' as const,
        label: group.groupName,
        value: `group_${group.groupId}`,
        color: getRandomColor(group.groupId),
        groupId: group.groupId,
      })),
    [groups],
  );

  // Default options (initial load - users and groups)
  const defaultOptions = useMemo(() => {
    const groupedOptions: GroupType[] = [];

    if (showUsers && users.length > 0) {
      groupedOptions.push({
        label: translate('team_members'),
        options: userOptions.map((uo) => ({
          ...uo,
          type: 'user',
        })),
      });
    }

    if (showGroups && groups.length > 0 && !restrictUserGroupsFeature) {
      groupedOptions.push({
        label: translate('groups'),
        options: groupOptions.map((go) => ({
          ...go,
          type: 'group',
        })),
      });
    }

    return groupedOptions;
  }, [
    showUsers,
    users.length,
    showGroups,
    groups.length,
    restrictUserGroupsFeature,
    translate,
    userOptions,
    groupOptions,
  ]);

  // Load options wrapper for async search (handles both users and groups)
  const loadOptionsWithGroups = useCallback(
    async (input: string) => {
      const userResults = await loadUserOptions(input);
      const groupedResults: GroupType[] = [];

      // Transform User[] to UserOption[]
      const transformedUsers = userResults.map((user) => ({
        type: 'user' as const,
        label: fullName(user),
        value: `user_${user.userId}`,
        imageUrl: user.photoUrl,
        userStatus: user.userStatus,
        description: user.role?.roleName,
        userId: user.userId,
      }));

      if (showUsers && transformedUsers.length > 0) {
        groupedResults.push({
          label: translate('team_members'),
          options: transformedUsers,
        });
      }

      if (showGroups && groups.length > 0 && !restrictUserGroupsFeature) {
        const filteredGroups = groups.filter((g) =>
          g.groupName.toLowerCase().includes(input.toLowerCase()),
        );

        if (filteredGroups.length > 0) {
          groupedResults.push({
            label: translate('groups'),
            options: filteredGroups.map((group) => ({
              type: 'group' as const,
              label: group.groupName,
              value: `group_${group.groupId}`,
              color: getRandomColor(group.groupId),
              groupId: group.groupId,
            })),
          });
        }
      }

      return groupedResults;
    },
    [
      loadUserOptions,
      showUsers,
      showGroups,
      groups,
      restrictUserGroupsFeature,
      translate,
    ],
  );

  const customComponents = {
    Option: UserGroupOption,
    SingleValue: UserGroupSingleValue,
    MultiValue: UserGroupMultiValue,
    MultiValueLabel: UserGroupMultiValueLabel,
    Group: GroupComponentForSelect,
  } as any;

  const selectComponent = (
    <AsyncSelect
      grouped
      noOptionsMessage={() => <FormattedMessage id="no.options" />}
      {...selectProps}
      isMulti
      defaultOptions={defaultOptions}
      loadOptions={loadOptionsWithGroups}
      cacheOptions
      components={customComponents}
      isSearchable
      placeholder={translate('placeholder.search')}
      closeMenuOnSelect={false}
      styles={selectStyles}
    />
  );

  return selectComponent;
};

UserGroupSelect.displayName = 'UserGroupSelect';
